import { Radio, Typography, useRadioGroup } from '@mui/material';
import type { FormikProps } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown } from '@/components/DropDown/DropDown';
import { FormTip } from '@/components/FormTip/FormTip';
import { styles } from '@/components/StyledComponents';
import { useEpisodesList } from '@/data/entities/episodes';
import { episodeImport } from '@/enums';
import { useRequiredAppSelector, useVersion } from '@/hooks';
import { ImportEpisode, ImportRadio } from './ImportScript.styles';
import type { TImportValues } from './ImportScriptForm';

interface IImportScriptEpisode {
    formik: FormikProps<TImportValues> | null;
    disabledOption?: boolean;
}

export const ImportScriptEpisode = ({ formik, disabledOption }: IImportScriptEpisode) => {
    const { t } = useTranslation();
    const projects_id = useRequiredAppSelector('projectId');
    const version = useVersion();
    const radioGroup = useRadioGroup();

    const { data } = useEpisodesList({ projectId: projects_id, version }, { enabled: !!projects_id });

    const episodesData = Array.isArray(data) ? data : [];
    const episodes = episodesData.map(item => {
        return { title: String(item.number), value: item.id };
    });

    useEffect(() => {
        if (episodes.length && formik && !formik.values.episodeId) formik.setFieldValue('episodeId', episodes[0]?.value);
    }, [formik, episodes[0]?.value, episodes.length]);

    return (
        <ImportEpisode>
            <ImportRadio control={<Radio />} value={episodeImport.current} label={t('import.options.episode')} disabled={disabledOption} />
            {formik && Boolean(formik.values.episodeId) && (
                <div className="w-full">
                    <Typography
                        variant="body1"
                        children={t('episode.number')}
                        color="primary.contrastText"
                        mb="5px"
                        sx={[Boolean(disabledOption) && { color: theme => theme.custom.c47 }]}
                    />
                    <DropDown
                        defaultValue={formik.values.episodeId}
                        items={episodes}
                        onChange={e => formik.setFieldValue('episodeId', e)}
                        sx={[
                            styles.dropdownAlt,
                            {
                                '.MuiSelect-select': { lineHeight: '46px' },
                                '.MuiSvgIcon-root': { top: '0.8em' },
                            },
                        ]}
                        disabled={radioGroup?.value !== episodeImport.current || disabledOption}
                    />
                </div>
            )}
            <FormTip children={t('import.episode-info')} disabled={disabledOption} />
        </ImportEpisode>
    );
};
